import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonBase, Fab, makeStyles } from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import AddPhotoAlternate from '@material-ui/icons/AddPhotoAlternate';
import { FormattedMessage, useIntl } from 'react-intl';
import Lightbox from './Lightbox';

const useStyles = makeStyles((theme) => ({
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  thumbnailWrapper: {
    position: 'relative',
    width: 136,
    height: 136,
    flexShrink: 0,
    '&:hover $removeButton': {
      opacity: 1,
    },
  },
  thumbnail: {
    width: '100%',
    height: '136px !important',
    objectFit: 'cover',
    borderRadius: 4,
    cursor: 'pointer',
  },
  removeButton: {
    position: 'absolute',
    right: -8,
    top: -8,
    width: 20,
    height: 20,
    minHeight: 20,
    boxShadow: theme.shadows[2],
    opacity: 0,
    transition: 'opacity 0.2s',
    '@media (hover: none)': {
      opacity: 1,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 14,
    },
  },
  imageButtonFill: {
    display: 'block',
    width: '100%',
    height: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
}));

const ImageGallery = ({ images, onDelete, onAdd, maxCount, readOnly }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const handleLightboxDelete = useCallback(
    (idx) => {
      if (readOnly || !onDelete) return;
      onDelete(idx);
    },
    [readOnly, onDelete],
  );

  if (!images || images.length === 0) {
    if (!readOnly && onAdd) {
      return (
        <Button variant="outlined" size="small" startIcon={<AddPhotoAlternate />} onClick={onAdd}>
          <FormattedMessage id="complaints.addImages" defaultMessage="Add images" />
        </Button>
      );
    }
    return null;
  }

  return (
    <>
      <div className={classes.grid}>
        {images.map((img, idx) => (
          <div key={img.id} className={classes.thumbnailWrapper}>
            <ButtonBase
              className={classes.imageButtonFill}
              onClick={() => openLightbox(idx)}
              aria-label={intl.formatMessage(
                { id: 'imageGallery.openImage', defaultMessage: 'Open image {index}' },
                { index: idx + 1 },
              )}
            >
              <img src={img.url} alt="" className={classes.thumbnail} />
            </ButtonBase>
            {!readOnly && onDelete && (
              <Fab
                className={classes.removeButton}
                onClick={() => onDelete(idx)}
                size="small"
                color="secondary"
                aria-label={intl.formatMessage({
                  id: 'imageGallery.removeImage',
                  defaultMessage: 'Remove image',
                })}
              >
                <Close />
              </Fab>
            )}
          </div>
        ))}
      </div>

      {!readOnly && onAdd && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddPhotoAlternate />}
          onClick={onAdd}
          disabled={maxCount != null && images.length >= maxCount}
        >
          <FormattedMessage id="complaints.addImages" defaultMessage="Add images" />
        </Button>
      )}

      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
          onDelete={handleLightboxDelete}
          readOnly={readOnly}
        />
      )}
    </>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      url: PropTypes.string.isRequired,
    }),
  ),
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  maxCount: PropTypes.number,
  readOnly: PropTypes.bool,
};

ImageGallery.defaultProps = {
  images: [],
  readOnly: false,
};

export default ImageGallery;
